import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { AuthContext } from '../../components/AuthContext';
import api from '../../api';
import Swal from 'sweetalert2';
import Select from 'react-select';
import {
  FaPlus, FaSearch, FaEdit, FaBan, FaCheckCircle, FaEye, FaUndo, FaPrint,
  FaCalendarAlt, FaInfoCircle, FaTimes, FaMoneyBillWave,
  FaFileInvoiceDollar, FaUser, FaFilter, FaSync,
} from 'react-icons/fa';

const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const todayISO = () => new Date().toISOString().split('T')[0];

const emptyForm = () => ({
  deliveryOrder: '',
  doType: 'SANITARY',
  returnDate: todayISO(),
  remarks: '',
  items: [], // { doItemId, name, units, rate, originalQuantity, priorReturnedQuantity, remainingQuantity, originalTTAmount, returnedQuantity, reasonForReturn }
  commissionCollection: { enabled: false, amountCollected: '', paymentDate: todayISO(), paymentModes: [], remarks: '' },
});

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const GoodsReturn = () => {
  const { user } = useContext(AuthContext);
  const employeeId = user?.user?._id;
  const companyId = user?.user?.company?._id || user?.user?.company;

  // Dark mode
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const i = setInterval(() => setDarkMode(localStorage.getItem('darkMode') === 'true'), 500);
    return () => clearInterval(i);
  }, []);

  const [returns, setReturns] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, total: 1, count: 0 });
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({ q: '', dateFrom: '', dateTo: '', doNumber: '', status: '', doType: '' });
  const [itemFilter, setItemFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [showDOModal, setShowDOModal] = useState(false);
  const [viewingDO, setViewingDO] = useState(null);
  const [loadingDO, setLoadingDO] = useState(false);

  // Eligible DO selection
  const [eligibleDOs, setEligibleDOs] = useState([]);
  const [eligibleSearch, setEligibleSearch] = useState('');
  const [eligibleDoType, setEligibleDoType] = useState('all');
  const [eligibleDateFrom, setEligibleDateFrom] = useState('');
  const [eligibleDateTo, setEligibleDateTo] = useState('');
  const [eligibleReference, setEligibleReference] = useState('');
  const [doContext, setDoContext] = useState(null); // { deliveryOrder, paidCommission, commissionContext }

  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // ─── Fetchers ────────────────────────────────────────────────────────────

  const fetchReturns = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (companyId) params.set('companyId', companyId);
      if (filters.q) params.set('q', filters.q);
      if (filters.doNumber) params.set('doNumber', filters.doNumber);
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      if (filters.status) params.set('status', filters.status);
      if (filters.doType) params.set('doType', filters.doType);
      const res = await api.get(`/goods-returns?${params}`);
      setReturns(res.data.data || []);
      setPagination(res.data.pagination || { current: 1, total: 1, count: 0 });
    } catch (err) {
      console.error('Failed to load goods returns:', err);
      setReturns([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, companyId, filters]);

  useEffect(() => { fetchReturns(); }, [fetchReturns]);

  const fetchEligibleDOs = useCallback(async (q = '', doType = 'all', dateFrom = '', dateTo = '', reference = '') => {
    try {
      const params = new URLSearchParams({ limit: 30 });
      if (companyId) params.set('companyId', companyId);
      if (q) params.set('q', q);
      if (doType && doType !== 'all') params.set('doType', doType);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (reference) params.set('reference', reference);
      const res = await api.get(`/goods-returns/eligible-delivery-orders?${params}`);
      setEligibleDOs(res.data.data || []);
    } catch (err) {
      console.error('Failed to load eligible DOs:', err);
      setEligibleDOs([]);
    }
  }, [companyId]);

  // Debounce search for eligible DOs
  useEffect(() => {
    if (!showFormModal) return;
    const t = setTimeout(() => fetchEligibleDOs(eligibleSearch, eligibleDoType, eligibleDateFrom, eligibleDateTo, eligibleReference), 250);
    return () => clearTimeout(t);
  }, [eligibleSearch, eligibleDoType, eligibleDateFrom, eligibleDateTo, eligibleReference, showFormModal, fetchEligibleDOs]);

  const openDOView = async (returnRow) => {
    setLoadingDO(true);
    setShowDOModal(true);
    setViewingDO(null);
    try {
      const res = await api.get(`/goods-returns/${returnRow._id}`);
      setViewingDO(res.data.data?.deliveryOrder || null);
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to load delivery order', 'error');
      setShowDOModal(false);
    } finally {
      setLoadingDO(false);
    }
  };

  const loadDOContext = async (doId, doType = 'SANITARY') => {
    try {
      const res = await api.get(`/goods-returns/by-delivery-order/${doId}?doType=${doType}`);
      const ctx = res.data.data;
      setDoContext(ctx);
      const dt = ctx.deliveryOrder.doType || doType;
      const items = (ctx.deliveryOrder.items || []).map(it => ({
        doItemId: String(it._id),
        name: it.name || it.Name || it.itemId?.Name || it.itemId?.name || 'Item',
        description: it.description || '',
        printType: it.printType || 'item',
        units: it.units || '',
        rate: Number(it.rate) || 0,
        originalQuantity: Number(it.quantity) || 0,
        priorReturnedQuantity: Number(it.priorReturnedQuantity) || 0,
        remainingQuantity: Number(it.remainingQuantity) || 0,
        originalAmountAfterDiscount: Number(it.amountAfterDiscount) || 0,
        originalTTAmount: dt === 'TILES' ? 0 : (Number(it.originalTTAmount || it.tt?.calculatedAmount) || 0),
        returnedQuantity: '',
        reasonForReturn: '',
      }));
      setForm(f => ({ ...f, deliveryOrder: doId, doType: dt, items }));
    } catch (err) {
      console.error('Failed to load DO context:', err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to load delivery order', 'error');
    }
  };

  // ─── Computed ────────────────────────────────────────────────────────────

  const totals = useMemo(() => {
    let amt = 0, tt = 0;
    for (const it of form.items) {
      const Q = Number(it.originalQuantity) || 0;
      const q = Number(it.returnedQuantity) || 0;
      const A = Number(it.originalAmountAfterDiscount) || 0;
      const T = Number(it.originalTTAmount) || 0;
      if (Q > 0 && q > 0) {
        amt += round2((A * q) / Q);
        tt += round2((T * q) / Q);
      }
    }
    return { totalReturnedAmount: round2(amt), totalTTReversed: round2(tt) };
  }, [form.items]);

  const collectionCap = useMemo(() => {
    if (!doContext?.commissionContext) return 0;
    const remainingFromPaid = doContext.commissionContext.remainingCollectable || 0;
    return round2(Math.min(totals.totalTTReversed, remainingFromPaid));
  }, [doContext, totals.totalTTReversed]);

  // ─── Actions ─────────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDoContext(null);
    setEligibleSearch('');
    setEligibleDoType('all');
    setEligibleDateFrom('');
    setEligibleDateTo('');
    setEligibleReference('');
    setShowFormModal(true);
  };

  const openEdit = async (row) => {
    try {
      setEditingId(row._id);
      const res = await api.get(`/goods-returns/${row._id}`);
      const r = res.data.data;
      // Re-load DO context to get remaining quantities (excluding *this* return is handled server-side via list aggregation, but for editing we just rebuild from DO and overlay current return values)
      const ctxRes = await api.get(`/goods-returns/by-delivery-order/${r.deliveryOrder._id || r.deliveryOrder}?doType=${r.doType || 'SANITARY'}`);
      const ctx = ctxRes.data.data;
      setDoContext(ctx);
      const itemMap = new Map((r.items || []).map(it => [String(it.doItemId), it]));
      const items = (ctx.deliveryOrder.items || []).map(it => {
        const cur = itemMap.get(String(it._id));
        return {
          doItemId: String(it._id),
          name: it.Name || it.itemId?.Name || it.itemId?.name || 'Item',
          description: it.description || '',
          printType: it.printType || 'item',
          units: it.units || '',
          rate: Number(it.rate) || 0,
          originalQuantity: Number(it.quantity) || 0,
          // Allow current return's quantity on top of remaining
          priorReturnedQuantity: Number(it.priorReturnedQuantity) || 0,
          remainingQuantity: round2((Number(it.remainingQuantity) || 0) + (Number(cur?.returnedQuantity) || 0)),
          originalAmountAfterDiscount: Number(it.amountAfterDiscount) || 0,
          originalTTAmount: (r.doType || ctx.deliveryOrder.doType) === 'TILES' ? 0 : (Number(it.originalTTAmount || it.tt?.calculatedAmount) || 0),
          returnedQuantity: cur ? cur.returnedQuantity : '',
          reasonForReturn: cur?.reasonForReturn || '',
        };
      });
      setForm({
        deliveryOrder: r.deliveryOrder._id || r.deliveryOrder,
        doType: r.doType || ctx.deliveryOrder.doType || 'SANITARY',
        returnDate: (r.returnDate || todayISO()).slice(0, 10),
        remarks: r.remarks || '',
        items,
        commissionCollection: r.commissionCollection
          ? {
              enabled: true,
              amountCollected: r.commissionCollection.amountCollected,
              paymentDate: (r.commissionCollection.paymentDate || todayISO()).slice(0, 10),
              paymentModes: r.commissionCollection.paymentModes || [],
              remarks: r.commissionCollection.remarks || '',
            }
          : { enabled: false, amountCollected: '', paymentDate: todayISO(), paymentModes: [], remarks: '' },
      });
      setShowFormModal(true);
    } catch (err) {
      console.error('Failed to open edit:', err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to open record for editing', 'error');
    }
  };

  const openView = async (row) => {
    try {
      const res = await api.get(`/goods-returns/${row._id}`);
      setViewing(res.data.data);
      setShowViewModal(true);
    } catch (err) {
      Swal.fire('Error', 'Failed to load goods return', 'error');
    }
  };

  const handleCancel = async (row) => {
    const result = await Swal.fire({
      title: 'Cancel this goods return?',
      text: `Return ${row.returnId} will be marked as Cancelled.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Yes, Cancel It',
    });
    if (!result.isConfirmed) return;
    try {
      await api.patch(`/goods-returns/${row._id}/cancel`, { cancelledBy: user?.user?._id });
      fetchReturns();
      Swal.fire({ icon: 'success', title: 'Cancelled', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to cancel', 'error');
    }
  };

  const handleRestore = async (row) => {
    const result = await Swal.fire({
      title: 'Restore this goods return?',
      text: `Return ${row.returnId} will be marked as Processed again.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#16a34a',
      confirmButtonText: 'Yes, Restore It',
    });
    if (!result.isConfirmed) return;
    try {
      await api.patch(`/goods-returns/${row._id}/restore`);
      fetchReturns();
      Swal.fire({ icon: 'success', title: 'Restored', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to restore', 'error');
    }
  };

  const updateItem = (idx, patch) => {
    setForm(f => {
      const next = [...f.items];
      next[idx] = { ...next[idx], ...patch };
      return { ...f, items: next };
    });
  };

  const handleSubmit = async () => {
    if (!form.deliveryOrder) return Swal.fire('Required', 'Please select a delivery order', 'warning');
    const itemsToSend = form.items
      .filter(it => Number(it.returnedQuantity) > 0)
      .map(it => ({
        doItemId: it.doItemId,
        returnedQuantity: Number(it.returnedQuantity),
        reasonForReturn: it.reasonForReturn || '',
      }));
    if (itemsToSend.length === 0) {
      return Swal.fire('Required', 'Enter a returned quantity on at least one item', 'warning');
    }
    // Client-side cap check
    for (const it of form.items) {
      const q = Number(it.returnedQuantity) || 0;
      if (q > it.remainingQuantity + 1e-6) {
        return Swal.fire('Invalid quantity', `"${it.name}" — returned ${q} exceeds remaining ${it.remainingQuantity}`, 'warning');
      }
    }
    // Commission recovery is handled automatically: any must-collect amount
    // defaults against the reference's advance balance; the remainder stays
    // pending for cash collection via the Reference Commission page.
    setSaving(true);
    try {
      const payload = {
        deliveryOrder: form.deliveryOrder,
        doType: form.doType || 'SANITARY',
        returnDate: form.returnDate,
        remarks: form.remarks,
        items: itemsToSend,
      };
      if (editingId) {
        payload.updatedBy = employeeId;
        await api.put(`/goods-returns/${editingId}`, payload);
      } else {
        payload.createdBy = employeeId;
        await api.post('/goods-returns', payload);
      }
      setShowFormModal(false);
      fetchReturns();
      Swal.fire({ icon: 'success', title: editingId ? 'Updated' : 'Created', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  const card = darkMode ? 'bg-gray-800 border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-900';
  const tableRow = darkMode ? 'border-gray-700 hover:bg-gray-700/40' : 'border-gray-200 hover:bg-gray-50';
  const inputCls = `w-full px-3 py-2 rounded border ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300'}`;
  const btnPrimary = 'inline-flex items-center gap-2 px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50';
  const btnSecondary = `inline-flex items-center gap-2 px-3 py-2 rounded border ${darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`;

  return (
    <div className={`p-4 md:p-6 min-h-full ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FaUndo /> Goods Return</h1>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
            Returns against Sanitary and Tiles delivery orders. Sanitary returns auto-reverse TT commission.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className={btnSecondary} onClick={fetchReturns}><FaSync /> Refresh</button>
          <button className={btnPrimary} onClick={openCreate}><FaPlus /> New Goods Return</button>
        </div>
      </div>

      {/* Filters */}
      <div className={`p-3 mb-4 rounded border ${card}`}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className={`pl-9 ${inputCls}`}
              placeholder="Search by Return ID, DO number, remarks…"
              value={filters.q}
              onChange={e => { setFilters(f => ({ ...f, q: e.target.value })); setPage(1); }}
            />
          </div>
          <input className={inputCls} placeholder="DO Number" value={filters.doNumber}
            onChange={e => { setFilters(f => ({ ...f, doNumber: e.target.value })); setPage(1); }} />
          <select className={inputCls} value={filters.status}
            onChange={e => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}>
            <option value="">All Statuses</option>
            <option value="Processed">Processed</option>
            <option value="Cancelled">Cancelled</option>
          </select>
          <input type="date" className={inputCls} value={filters.dateFrom}
            onChange={e => { setFilters(f => ({ ...f, dateFrom: e.target.value })); setPage(1); }} />
          <input type="date" className={inputCls} value={filters.dateTo}
            onChange={e => { setFilters(f => ({ ...f, dateTo: e.target.value })); setPage(1); }} />
          <select className={inputCls} value={filters.doType}
            onChange={e => { setFilters(f => ({ ...f, doType: e.target.value })); setPage(1); }}>
            <option value="">All Types</option>
            <option value="SANITARY">Sanitary</option>
            <option value="TILES">Tiles</option>
          </select>
          <button
            className={`${btnSecondary} flex items-center justify-center gap-1`}
            onClick={() => { setFilters({ q: '', dateFrom: '', dateTo: '', doNumber: '', status: '', doType: '' }); setItemFilter(''); setPage(1); }}
          >
            <FaTimes /> Reset
          </button>
        </div>
        {/* Item filter */}
        <div className="mt-2 relative">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className={`pl-9 ${inputCls}`}
            placeholder="Filter by item name…"
            value={itemFilter}
            onChange={e => setItemFilter(e.target.value)}
          />
        </div>
      </div>

      {/* List */}
      <div className={`rounded border overflow-x-auto ${card}`}>
        <table className="w-full text-sm">
          <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}>
            <tr>
              <th className="text-left px-3 py-2">Return ID</th>
              <th className="text-left px-3 py-2">Return Date</th>
              <th className="text-left px-3 py-2">DO Issued</th>
              <th className="text-left px-3 py-2">DO Number</th>
              <th className="text-left px-3 py-2">Recipient</th>
              <th className="text-left px-3 py-2">Reference</th>
              <th className="text-right px-3 py-2">Returned ₹</th>
              <th className="text-right px-3 py-2">TT Reversed</th>
              <th className="text-right px-3 py-2">TT Collected</th>
              <th className="text-left px-3 py-2">By</th>
              <th className="text-center px-3 py-2">Status</th>
              <th className="text-center px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} className="text-center py-8">Loading…</td></tr>
            ) : returns.length === 0 ? (
              <tr><td colSpan={12} className="text-center py-8 text-gray-500">No goods returns yet</td></tr>
            ) : returns.filter(r => !itemFilter.trim() || r.items?.some(it => {
              const q = itemFilter.trim().toLowerCase();
              return it.name?.toLowerCase().includes(q) || it.description?.toLowerCase().includes(q);
            })).map(r => {
              const recipient = r.deliveryOrder?.recipient?.businessName || r.deliveryOrder?.recipient?.name || '—';
              return (
                <tr key={r._id} className={`border-t ${tableRow}`}>
                  <td className="px-3 py-2 font-mono">
                    {r.returnId}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div>{r.returnDate ? new Date(r.returnDate).toLocaleDateString() : '—'}</div>
                    <div className="text-xs text-gray-500">{r.createdAt ? new Date(r.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div>{r.deliveryOrder?.createdAt ? new Date(r.deliveryOrder.createdAt).toLocaleDateString() : '—'}</div>
                    <div className="text-xs text-gray-500">{r.deliveryOrder?.createdAt ? new Date(r.deliveryOrder.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</div>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      className="font-mono text-blue-600 hover:underline"
                      onClick={() => openDOView(r)}
                    >{r.doNumber}</button>
                  </td>
                  <td className="px-3 py-2">{recipient}</td>
                  <td className="px-3 py-2">{r.reference?.name || '—'}</td>
                  <td className="px-3 py-2 text-right">{inr(r.totalReturnedAmount)}</td>
                  <td className="px-3 py-2 text-right text-orange-600">{inr(r.totalTTReversed)}</td>
                  <td className="px-3 py-2 text-right text-green-600">{inr(r.commissionCollection?.amountCollected)}</td>
                  <td className="px-3 py-2">{r.createdBy?.name || '—'}</td>
                  <td className="px-3 py-2 text-center whitespace-nowrap">
                    {r.status === 'Cancelled' ? (
                      <button
                        title="Click to restore to Processed"
                        onClick={() => handleRestore(r)}
                        className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700 hover:bg-red-200 cursor-pointer"
                      >Cancelled</button>
                    ) : (
                      <button
                        title="Click to cancel this goods return"
                        onClick={() => handleCancel(r)}
                        className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer"
                      >Processed</button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center whitespace-nowrap">
                    <button title="View" className="p-1 mx-1 text-blue-600 hover:underline" onClick={() => openView(r)}><FaEye /></button>
                    <button title="Print" className="p-1 mx-1 text-purple-600 hover:underline" onClick={() => window.open(`/goods-return/${r._id}`, '_blank')}><FaPrint /></button>
                    <button title="Edit" className="p-1 mx-1 text-amber-600 hover:underline" onClick={() => openEdit(r)}><FaEdit /></button>
                    {r.status === 'Processed' ? (
                      <button
                        title="Cancel this goods return"
                        className="p-1 mx-1 text-red-600 hover:underline"
                        onClick={() => handleCancel(r)}
                      ><FaBan /></button>
                    ) : (
                      <button
                        title="Mark as Processed (restore)"
                        className="p-1 mx-1 text-green-600 hover:underline"
                        onClick={() => handleRestore(r)}
                      ><FaCheckCircle /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-3">
        <div className="text-sm text-gray-500">
          {pagination.count} record{pagination.count === 1 ? '' : 's'} · page {pagination.current} of {pagination.total}
        </div>
        <div className="flex gap-2">
          <button className={btnSecondary} disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</button>
          <button className={btnSecondary} disabled={page >= pagination.total} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      </div>

      {/* ────── Form Modal ────── */}
      {showFormModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3">
          <div className={`w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${card} border`}>
            <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <FaUndo /> {editingId ? 'Edit Goods Return' : 'New Goods Return'}
              </h2>
              <button onClick={() => setShowFormModal(false)} className="p-2 rounded hover:bg-gray-200/30"><FaTimes /></button>
            </div>

            <div className="p-4 space-y-4">
              {/* Step 1: pick DO */}
              {!doContext && (
                <div>
                  <label className="block text-sm font-medium mb-1">Search Delivery Order</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      className={inputCls}
                      placeholder="DO number, manual DO number, invoice…"
                      value={eligibleSearch}
                      onChange={e => setEligibleSearch(e.target.value)}
                      autoFocus
                    />
                    <select
                      className={inputCls}
                      style={{ minWidth: 120, width: 'auto', flex: '0 0 auto' }}
                      value={eligibleDoType}
                      onChange={e => setEligibleDoType(e.target.value)}
                    >
                      <option value="all">All Types</option>
                      <option value="SANITARY">Sanitary</option>
                      <option value="TILES">Tiles</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-1">
                    <div>
                      <label className="block text-xs text-gray-500 mb-0.5">From</label>
                      <input type="date" className={inputCls} value={eligibleDateFrom} onChange={e => setEligibleDateFrom(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-0.5">To</label>
                      <input type="date" className={inputCls} value={eligibleDateTo} onChange={e => setEligibleDateTo(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-0.5">Reference</label>
                      <input className={inputCls} placeholder="Reference name…" value={eligibleReference} onChange={e => setEligibleReference(e.target.value)} />
                    </div>
                  </div>
                  <div className={`mt-2 max-h-72 overflow-y-auto rounded border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    {eligibleDOs.length === 0 ? (
                      <div className="p-4 text-sm text-gray-500 text-center">No matching delivery orders</div>
                    ) : eligibleDOs.map(o => (
                      <button
                        key={o._id}
                        onClick={() => loadDOContext(o._id, o.doType || 'SANITARY')}
                        className={`w-full text-left px-3 py-2 border-b ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50'}`}
                      >
                        <div className="flex justify-between gap-3">
                          <span className="font-mono">{o.doNumber}</span>
                          <span className="text-xs text-gray-500">{o.status}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {o.recipient?.businessName || o.recipient?.name || ''} · {o.doType || 'SANITARY'} · ref: {o.reference?.name || '—'} · {inr(o.totalAmountWithGST)}{o.doType !== 'TILES' ? ` · TT ${inr(o.totalTTAmount)}` : ''}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: items */}
              {doContext && (
                <>
                  <div className={`p-3 rounded border ${darkMode ? 'border-gray-700 bg-gray-700/30' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div><span className="text-gray-500">DO:</span> <span className="font-mono">{doContext.deliveryOrder.doNumber}</span></div>
                      <div><span className="text-gray-500">Recipient:</span> {doContext.deliveryOrder.recipient?.businessName || doContext.deliveryOrder.recipient?.name || '—'}</div>
                      <div><span className="text-gray-500">Type:</span> <span className={`font-semibold ${form.doType === 'TILES' ? 'text-blue-500' : 'text-amber-500'}`}>{form.doType}</span></div>
                      {form.doType !== 'TILES' && <div><span className="text-gray-500">DO Total TT:</span> {inr(doContext.deliveryOrder.totalTTAmount)}</div>}
                    </div>
                    <div className="mt-2 flex gap-3 items-center">
                      <button className={btnSecondary} onClick={() => { setDoContext(null); setForm(emptyForm()); }}>
                        Change DO
                      </button>
                      <div className="flex items-center gap-2">
                        <FaCalendarAlt />
                        <input type="date" className={inputCls} style={{ width: 'auto' }}
                          value={form.returnDate}
                          onChange={e => setForm(f => ({ ...f, returnDate: e.target.value }))} />
                      </div>
                    </div>
                  </div>

                  <div className={`rounded border overflow-x-auto ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <table className="w-full text-sm">
                      <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                        <tr>
                          <th className="text-left px-2 py-1">Item</th>
                          <th className="text-right px-2 py-1">Original Qty</th>
                          <th className="text-right px-2 py-1">Already Returned</th>
                          <th className="text-right px-2 py-1">Remaining</th>
                          {form.doType !== 'TILES' && <th className="text-right px-2 py-1">Original TT</th>}
                          <th className="text-right px-2 py-1">Return Qty</th>
                          {form.doType !== 'TILES' && <th className="text-right px-2 py-1">TT Reversed</th>}
                          <th className="text-left px-2 py-1">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.items.map((it, idx) => {
                          const Q = it.originalQuantity, q = Number(it.returnedQuantity) || 0;
                          const ttRev = Q > 0 && q > 0 ? round2((Number(it.originalTTAmount) * q) / Q) : 0;
                          const qtyOverLimit = it.returnedQuantity !== '' && q > it.remainingQuantity;
                          const qtyNegative = it.returnedQuantity !== '' && q < 0;
                          const qtyInvalid = qtyOverLimit || qtyNegative;
                          return (
                            <tr key={it.doItemId} className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                              <td className="px-2 py-1" style={{minWidth: 160}}>
                                {(() => {
                                  const pt = it.printType || 'item';
                                  const n = it.name || it.description || '—';
                                  const d = it.description || '';
                                  if (pt === 'description') return d || <strong>{n}</strong>;
                                  if (pt === 'both') return <><strong>{n}</strong>{d ? <><br /><span className="text-xs">{d}</span></> : ''}  </>;
                                  return <strong>{n}</strong>;
                                })()}
                                {' '}<span className="text-xs text-gray-500">{it.units}</span>
                              </td>
                              <td className="px-2 py-1 text-right">{it.originalQuantity}</td>
                              <td className="px-2 py-1 text-right text-orange-600">{it.priorReturnedQuantity}</td>
                              <td className={`px-2 py-1 text-right font-medium ${qtyOverLimit ? 'text-red-500' : ''}`}>{it.remainingQuantity}</td>
                              {form.doType !== 'TILES' && <td className="px-2 py-1 text-right">{inr(it.originalTTAmount)}</td>}
                              <td className="px-2 py-1 text-right">
                                <input
                                  type="number" min="0" max={it.remainingQuantity} step="0.001"
                                  className={`${inputCls} text-right ${qtyInvalid ? 'border-red-500 focus:ring-red-500' : ''}`}
                                  style={{ width: 110 }}
                                  value={it.returnedQuantity}
                                  onChange={e => updateItem(idx, { returnedQuantity: e.target.value })}
                                  onBlur={e => {
                                    const val = Number(e.target.value);
                                    if (val > it.remainingQuantity) updateItem(idx, { returnedQuantity: it.remainingQuantity });
                                    else if (val < 0) updateItem(idx, { returnedQuantity: 0 });
                                  }}
                                />
                                {qtyOverLimit && <div className="text-red-500 text-xs mt-0.5">Max {it.remainingQuantity}</div>}
                                {qtyNegative && <div className="text-red-500 text-xs mt-0.5">Must be ≥ 0</div>}
                              </td>
                              {form.doType !== 'TILES' && <td className="px-2 py-1 text-right text-orange-700">{inr(ttRev)}</td>}
                              <td className="px-2 py-1">
                                <input
                                  className={inputCls}
                                  placeholder="reason (optional)"
                                  value={it.reasonForReturn}
                                  onChange={e => updateItem(idx, { reasonForReturn: e.target.value })}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className={darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}>
                          <td colSpan={form.doType === 'TILES' ? 4 : 5} className="px-2 py-1 text-right font-medium">Totals</td>
                          <td className="px-2 py-1 text-right font-bold">—</td>
                          {form.doType !== 'TILES' && <td className="px-2 py-1 text-right font-bold text-orange-700">{inr(totals.totalTTReversed)}</td>}
                          <td className="px-2 py-1"></td>
                        </tr>
                        <tr className={darkMode ? 'bg-gray-700/30' : 'bg-gray-100'}>
                          <td colSpan={form.doType === 'TILES' ? 4 : 5} className="px-2 py-1 text-right text-sm text-gray-500">Returned amount (post-discount)</td>
                          <td colSpan={form.doType === 'TILES' ? 1 : 2} className="px-2 py-1 text-right font-semibold">{inr(totals.totalReturnedAmount)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* TT Collection info — SANITARY only. Collection is automatic:
                      must-collect defaults against the reference's advance balance;
                      any remainder is collected as cash from the Reference Commission page. */}
                  {form.doType !== 'TILES' && doContext.commissionContext?.hasPaidCommission && (
                  <div className={`p-3 rounded border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-semibold flex items-center gap-2">
                        <FaMoneyBillWave className="text-green-600" /> Commission on Returned Goods
                      </h3>
                    </div>
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-500">
                      <div>Paid amount: <span className={darkMode ? 'text-gray-200' : 'text-gray-800'}>{inr(doContext.commissionContext.paidAmount)}</span></div>
                      <div>Already collected: <span className={darkMode ? 'text-gray-200' : 'text-gray-800'}>{inr(doContext.commissionContext.totalCollectedSoFar)}</span></div>
                      <div>Remaining (paid): <span className={darkMode ? 'text-gray-200' : 'text-gray-800'}>{inr(doContext.commissionContext.remainingCollectable)}</span></div>
                      <div>Cap (this return): <span className="font-medium text-green-700">{inr(collectionCap)}</span></div>
                    </div>
                    <p className="mt-2 text-xs text-gray-500 flex items-start gap-1">
                      <FaInfoCircle className="mt-0.5 shrink-0" />
                      Any commission already paid on the returned quantity becomes a Receivable owed by the reference — auto-adjusted on their next commission assignment or collected as cash in Reference Commission.
                    </p>
                  </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium mb-1">Remarks</label>
                    <textarea className={inputCls} rows={2} value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
                  </div>
                </>
              )}
            </div>

            <div className={`flex justify-end gap-2 p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button className={btnSecondary} onClick={() => setShowFormModal(false)} disabled={saving}>Cancel</button>
              <button className={btnPrimary} onClick={handleSubmit} disabled={saving || !doContext}>
                {saving ? 'Saving…' : (editingId ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ────── View Modal ────── */}
      {showViewModal && viewing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3">
          <div className={`w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${card} border`}>
            <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className="text-lg font-semibold flex items-center gap-2"><FaFileInvoiceDollar /> Goods Return {viewing.returnId}</h2>
              <button onClick={() => setShowViewModal(false)} className="p-2 rounded hover:bg-gray-200/30"><FaTimes /></button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <div><span className="text-gray-500">Date:</span> {viewing.returnDate ? new Date(viewing.returnDate).toLocaleDateString() : ''}</div>
                <div><span className="text-gray-500">DO:</span> <span className="font-mono">{viewing.doNumber}</span></div>
                <div><span className="text-gray-500">Type:</span> <span className={`font-semibold ${viewing.doType === 'TILES' ? 'text-blue-500' : 'text-amber-500'}`}>{viewing.doType || 'SANITARY'}</span></div>
                <div><span className="text-gray-500">Reference:</span> {viewing.reference?.name || '—'}</div>
                <div><span className="text-gray-500">Created By:</span> {viewing.createdBy?.name || '—'}</div>
                <div><span className="text-gray-500">Total Returned ₹:</span> {inr(viewing.totalReturnedAmount)}</div>
                {(viewing.doType || 'SANITARY') !== 'TILES' && <div><span className="text-gray-500">TT Reversed:</span> <span className="text-orange-700">{inr(viewing.totalTTReversed)}</span></div>}
              </div>
              <div className="rounded border overflow-x-auto">
                <table className="w-full">
                  <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                    <tr>
                      <th className="text-left px-2 py-1">Item</th>
                      <th className="text-left px-2 py-1">Print Type</th>
                      {(viewing.doType || 'SANITARY') !== 'TILES' && <th className="text-right px-2 py-1">TT Reversed</th>}
                      <th className="text-left px-2 py-1">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewing.items.map(it => (
                      <tr key={it._id} className="border-t">
                        <td className="px-2 py-1" style={{minWidth: 160}}>
                          {(() => {
                            const pt = it.printType || 'item';
                            const n = it.name || it.description || '—';
                            const d = it.description || '';
                            if (pt === 'description') return d || <strong>{n}</strong>;
                            if (pt === 'both') return <><strong>{n}</strong>{d ? <><br /><span className="text-xs">{d}</span></> : ''}</>;
                            return <strong>{n}</strong>;
                          })()}
                        </td>
                        <td className="px-2 py-1">
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                            it.printType === 'description' ? 'bg-purple-100 text-purple-700' :
                            it.printType === 'both' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>{it.printType || 'item'}</span>
                        </td>
                        <td className="px-2 py-1 text-right">{it.originalQuantity}</td>
                        <td className="px-2 py-1 text-right">{it.returnedQuantity}</td>
                        <td className="px-2 py-1 text-right">{inr(it.amountReturned)}</td>
                        {(viewing.doType || 'SANITARY') !== 'TILES' && <td className="px-2 py-1 text-right text-orange-700">{inr(it.ttReversedAmount)}</td>}
                        <td className="px-2 py-1">{it.reasonForReturn}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {viewing.commissionCollection && (
                <div className="rounded border p-3">
                  <h4 className="font-semibold flex items-center gap-2 mb-2"><FaMoneyBillWave className="text-green-600" /> TT Collection</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div><span className="text-gray-500">Amount:</span> <span className="text-green-700 font-medium">{inr(viewing.commissionCollection.amountCollected)}</span></div>
                    <div><span className="text-gray-500">Date:</span> {viewing.commissionCollection.paymentDate ? new Date(viewing.commissionCollection.paymentDate).toLocaleDateString() : ''}</div>
                    <div className="md:col-span-2"><span className="text-gray-500">Modes:</span> {(viewing.commissionCollection.paymentModes || []).join(', ') || '—'}</div>
                    {viewing.commissionCollection.remarks && (
                      <div className="md:col-span-4"><span className="text-gray-500">Remarks:</span> {viewing.commissionCollection.remarks}</div>
                    )}
                  </div>
                </div>
              )}
              {viewing.remarks && (
                <div><span className="text-gray-500">Remarks:</span> {viewing.remarks}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ────── DO View Modal ────── */}
      {showDOModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3">
          <div className={`w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${card} border`}>
            <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <FaFileInvoiceDollar /> Delivery Order
                {viewingDO && <span className="font-mono text-base ml-1">{viewingDO.doNumber}</span>}
              </h2>
              <button onClick={() => setShowDOModal(false)} className="p-2 rounded hover:bg-gray-200/30"><FaTimes /></button>
            </div>
            <div className="p-4 text-sm">
              {loadingDO ? (
                <div className="text-center py-10">Loading…</div>
              ) : !viewingDO ? (
                <div className="text-center py-10 text-gray-500">No delivery order data</div>
              ) : (
                <div className="space-y-4">
                  {/* Header info */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <div><span className="text-gray-500">DO Number:</span> <span className="font-mono">{viewingDO.doNumber}</span></div>
                    {viewingDO.Manual_dO_Number && <div><span className="text-gray-500">Manual DO:</span> {viewingDO.Manual_dO_Number}</div>}
                    <div><span className="text-gray-500">Type:</span> <span className={`font-semibold ${viewingDO.typeOfDeliveryOrder === 'TILES' ? 'text-blue-500' : 'text-amber-500'}`}>{viewingDO.typeOfDeliveryOrder || '—'}</span></div>
                    <div><span className="text-gray-500">Status:</span> {viewingDO.status}</div>
                    <div><span className="text-gray-500">Date:</span> {viewingDO.createdAt ? new Date(viewingDO.createdAt).toLocaleDateString() : '—'}</div>
                    <div><span className="text-gray-500">Total:</span> <span className="font-semibold">{inr(viewingDO.totalAmountWithGST)}</span></div>
                    {viewingDO.totalTTAmount > 0 && <div><span className="text-gray-500">Total TT:</span> <span className="text-orange-600">{inr(viewingDO.totalTTAmount)}</span></div>}
                  </div>
                  {/* Recipient */}
                  {viewingDO.recipient && (
                    <div className={`rounded border p-3 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <h4 className="font-semibold mb-2 flex items-center gap-2"><FaUser className="text-blue-500" /> Recipient</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {viewingDO.recipient.name && <div><span className="text-gray-500">Name:</span> {viewingDO.recipient.name}</div>}
                        {viewingDO.recipient.businessName && <div><span className="text-gray-500">Business:</span> {viewingDO.recipient.businessName}</div>}
                        {viewingDO.recipient.phone && <div><span className="text-gray-500">Phone:</span> {viewingDO.recipient.phone}</div>}
                        {viewingDO.recipient.gstNo && <div><span className="text-gray-500">GST:</span> {viewingDO.recipient.gstNo}</div>}
                        {viewingDO.recipient.siteAddress && <div className="md:col-span-2"><span className="text-gray-500">Address:</span> {viewingDO.recipient.siteAddress}</div>}
                      </div>
                    </div>
                  )}
                  {/* Items */}
                  <div className="rounded border overflow-x-auto">
                    <table className="w-full">
                      <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                        <tr>
                          <th className="text-left px-2 py-1">#</th>
                          <th className="text-left px-2 py-1">Item</th>
                          <th className="text-right px-2 py-1">Qty</th>
                          <th className="text-left px-2 py-1">Units</th>
                          <th className="text-right px-2 py-1">Rate</th>
                          <th className="text-right px-2 py-1">Discount%</th>
                          <th className="text-right px-2 py-1">Amount</th>
                          {viewingDO.typeOfDeliveryOrder !== 'TILES' && <th className="text-right px-2 py-1">TT</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {(viewingDO.items || []).map((it, i) => (
                          <tr key={it._id || i} className="border-t">
                            <td className="px-2 py-1">{i + 1}</td>
                            <td className="px-2 py-1" style={{minWidth: 160}}>
                              {(() => {
                                const name = it.itemId?.Name || it.itemId?.name || it.Name || '';
                                const desc = it.description || '';
                                const n = name || desc || '—';
                                const pt = it.printType || 'item';
                                if (pt === 'description') return desc || <strong>{n}</strong>;
                                if (pt === 'both') return <><strong>{n}</strong>{desc ? <><br /><span className="text-xs">{desc}</span></> : ''}</>;
                                return <strong>{n}</strong>;
                              })()}
                            </td>
                            <td className="px-2 py-1 text-right">{it.quantity}</td>
                            <td className="px-2 py-1">{it.units}</td>
                            <td className="px-2 py-1 text-right">{inr(it.rate)}</td>
                            <td className="px-2 py-1 text-right">{it.discount || 0}%</td>
                            <td className="px-2 py-1 text-right">{inr(it.amountAfterDiscount)}</td>
                            {viewingDO.typeOfDeliveryOrder !== 'TILES' && <td className="px-2 py-1 text-right text-orange-600">{inr(it.tt?.calculatedAmount)}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoodsReturn;